import * as React from 'react';
import { FieldErrors, Path, useFieldArray, useFormContext } from 'react-hook-form';
import { TOwner, TOwnershipStructure } from '../../../types';
import { Box, Button, Collapse, Divider, Typography } from '@mui/material';
import { ForbidenCountryOwnerCard } from './ForbidenCountryOwnerCard';
import { Add, ExpandMore } from '@mui/icons-material';
import {
  EMPTY_FORBIDEN_COUNTRY_LEGAL_ENTITY,
  EMPTY_FORBIDEN_COUNTRY_INDIVIDUAL,
} from '../../../constants';
import { useState } from 'react';
import { commonStyles } from '~/theme/styles/commonStyles';

interface IForbidenCountriesOwnersProps {
  ownerIndex: number;
}

export const ForbidenCountriesOwners = React.memo(
  ({ ownerIndex }: IForbidenCountriesOwnersProps) => {
    const {
      control,
      formState: { errors },
      trigger,
    } = useFormContext<TOwnershipStructure>();

    const [expanded, setExpanded] = useState(true);
    const [expanded1, setExpanded1] = useState(true);

    const error = (errors.owners?.[ownerIndex] as FieldErrors<TOwner & { forbidenOwners: string }>)
      ?.forbidenOwners?.message;

    const {
      fields: russianBelarusianIndividuals,
      append: addIndividual,
      remove: removeIndividual,
    } = useFieldArray({
      control,
      name: `owners.${ownerIndex}.russianBelarusianIndividuals`,
    });
    const {
      fields: russianBelarusianLegalEntities,
      append: addLegalEntity,
      remove: removeLegalEntity,
    } = useFieldArray({
      control,
      name: `owners.${ownerIndex}.russianBelarusianLegalEntities`,
    });

    const handleAddLegalEntity = () => {
      addLegalEntity(EMPTY_FORBIDEN_COUNTRY_LEGAL_ENTITY);
      trigger(`owners.${ownerIndex}.forbidenOwners` as Path<TOwnershipStructure>);
    };

    const handleAddIndividual = () => {
      addIndividual(EMPTY_FORBIDEN_COUNTRY_INDIVIDUAL);
      trigger(`owners.${ownerIndex}.forbidenOwners` as Path<TOwnershipStructure>);
    };

    const handleDeleteIndividual = (index: number) => {
      removeIndividual(index);
      trigger(`owners.${ownerIndex}.forbidenOwners` as Path<TOwnershipStructure>);
    };

    const handleDeleteLegalResident = (index: number) => {
      removeLegalEntity(index);
      trigger(`owners.${ownerIndex}.forbidenOwners` as Path<TOwnershipStructure>);
    };

    return (
      <Box sx={commonStyles.columnFlexContainer}>
        <Box sx={{ mb: 1, position: 'relative' }}>
          <Typography variant="body1">
            Вкажіть осіб - громадян та/aбо резидентів РФ чи РБ
          </Typography>
          {!!error && (
            <Typography
              sx={{ mb: 1, position: 'absolute', bottom: -14, color: 'error.main' }}
              variant="caption"
            >
              {error}
            </Typography>
          )}
        </Box>

        <Box sx={commonStyles.centerFlexContainer}>
          <ExpandMore
            onClick={() => setExpanded(!expanded)}
            sx={{ transform: `rotate(${expanded ? 0 : -90}deg)`, cursor: 'pointer' }}
          />
          <Typography>Фізичні особи</Typography>
        </Box>
        <Collapse in={expanded}>
          <Box sx={commonStyles.columnFlexContainer}>
            {russianBelarusianIndividuals.map((owner, index) => (
              <ForbidenCountryOwnerCard
                prefix={`owners.${ownerIndex}.russianBelarusianIndividuals`}
                handleDelete={handleDeleteIndividual}
                key={owner.id}
                index={index}
                isError={
                  !!(errors.owners?.[ownerIndex] as FieldErrors<TOwner>)
                    ?.russianBelarusianIndividuals?.[index]
                }
              />
            ))}
            <Button startIcon={<Add />} variant="outlined" onClick={handleAddIndividual}>
              Додати ФО
            </Button>
          </Box>
        </Collapse>

        <Divider sx={{ mb: 2 }} />

        <Box sx={commonStyles.centerFlexContainer}>
          <ExpandMore
            onClick={() => setExpanded1(!expanded1)}
            sx={{ transform: `rotate(${expanded1 ? 0 : -90}deg)`, cursor: 'pointer' }}
          />
          <Typography>Юридичні особи</Typography>
        </Box>

        <Collapse in={expanded1}>
          <Box sx={commonStyles.columnFlexContainer}>
            {russianBelarusianLegalEntities.map((owner, index) => (
              <ForbidenCountryOwnerCard
                prefix={`owners.${ownerIndex}.russianBelarusianLegalEntities`}
                handleDelete={handleDeleteLegalResident}
                key={owner.id}
                isLegalEntity
                index={index}
                isError={
                  !!(errors.owners?.[ownerIndex] as FieldErrors<TOwner>)
                    ?.russianBelarusianLegalEntities?.[index]
                }
              />
            ))}
            <Button startIcon={<Add />} variant="outlined" onClick={handleAddLegalEntity}>
              Додати ЮО
            </Button>
          </Box>
        </Collapse>
      </Box>
    );
  }
);
